import { z } from 'zod';
import {
  normalizedUuidSchema,
  paginatedSchema,
  paginationSchema,
  UUID_PATTERN,
  uuidSchema,
} from './common';
import { CATALOG_UPLOAD_PURPOSE_SEGMENTS, IMAGE_CONTENT_TYPE_EXTENSIONS } from './storage';

// ---------------------------------------------------------------------------
// catalog 도메인 계약 — 상품·카테고리·인쇄 방식. 전 프로시저 ADMIN 전용.
// 설계 결정: docs/design/catalog-admin-pipeline-plan.md
// ---------------------------------------------------------------------------

export const PRODUCT_STATUSES = ['DRAFT', 'PUBLISHED', 'HIDDEN'] as const;
export type ProductStatus = (typeof PRODUCT_STATUSES)[number];

/**
 * 상품 실측 종류. schema.prisma 의 ProductMeasurementKind 와 일치해야 한다(api 스펙이 검사).
 * CYLINDER 면 widthMm 는 지름이다(정책 §2-1, ADR-0007 D3).
 */
export const PRODUCT_MEASUREMENT_KINDS = ['FLAT', 'CYLINDER'] as const;
export type ProductMeasurementKind = (typeof PRODUCT_MEASUREMENT_KINDS)[number];
export const PRODUCT_MEASUREMENT_KIND_LABELS: Readonly<Record<ProductMeasurementKind, string>> = {
  FLAT: '평면형',
  CYLINDER: '곡면형',
};
/** 실측 종류 선택 화면의 설명 줄. */
export const PRODUCT_MEASUREMENT_KIND_DESCRIPTIONS: Readonly<
  Record<ProductMeasurementKind, string>
> = {
  FLAT: '사진에 보이는 그대로 잽니다. 예: 티셔츠, 에코백, 키링, 우산(위에서 본 사진)',
  CYLINDER: '둘러 가며 인쇄되는 상품. 예: 머그, 텀블러, 볼캡',
};

/** 사용자 색상 필터 전용 표준 색상군. schema.prisma 의 ColorFamily 와 일치해야 한다. */
export const COLOR_FAMILIES = [
  'BLACK',
  'WHITE',
  'GRAY',
  'RED',
  'ORANGE',
  'YELLOW',
  'GREEN',
  'BLUE',
  'PURPLE',
  'PINK',
  'BROWN',
  'BEIGE',
  'MULTI',
] as const;
export type ColorFamily = (typeof COLOR_FAMILIES)[number];

/** 표준 색상군의 한글 노출 라벨 — web(필터)·admin(색상 폼)이 공유하는 단일 원천. */
export const COLOR_FAMILY_LABELS: Readonly<Record<ColorFamily, string>> = {
  BLACK: '블랙',
  WHITE: '화이트',
  GRAY: '그레이',
  RED: '레드',
  ORANGE: '오렌지',
  YELLOW: '옐로우',
  GREEN: '그린',
  BLUE: '블루',
  PURPLE: '퍼플',
  PINK: '핑크',
  BROWN: '브라운',
  BEIGE: '베이지',
  MULTI: '멀티',
};

export const MAX_PRODUCT_IMAGES = 5;
export const MAX_COLOR_OPTIONS = 30;
export const MAX_PRICE_TIERS = 20;
export const MAX_UNIT_PRICE = 100_000_000;
/**
 * 수량(최소 주문 수량·가격 구간 시작 수량)의 도메인 상한. 컬럼이 INT4 라 범위 밖 값은
 * DB 오류(5xx)가 되므로 경계에서 400 으로 거부한다. 공개 필터 상한과 같은 값을 공유한다.
 */
export const MAX_MIN_ORDER_QTY = 1_000_000;
/** 추천 순위 상한 — INT4 방어. 10 단위 간격 운영이라 실사용은 수천 이내다. */
export const MAX_RECOMMENDED_RANK = 1_000_000;
/** HOME 추천 상품 수 — 순위 있는 상품의 최대 개수이자 HOME 추천 목록 길이(SUPER-60 확정). */
export const MAX_RECOMMENDED_PRODUCTS = 8;
/** 랜딩 화면이 추천 순위를 다시 매길 때의 간격 — 상품 목록 표에서 사이 값을 넣을 수 있게 띄운다. */
export const RECOMMENDED_RANK_STEP = 10;

/** 가격은 KRW 정수(원). 소수 통화가 아니므로 number 로 안전하다. (DB 는 Decimal) */
export const unitPriceSchema = z
  .number()
  .int('단가는 원 단위 정수여야 합니다.')
  .min(1)
  .max(MAX_UNIT_PRICE);

/**
 * presign 이 발급하는 키 형식 전체 — `products/{상품ID}/{용도}/{uuid}.{확장자}`.
 * 접두어만 고정하면 `products/{내 상품}/images/../../{남의 상품}/...` 처럼 접두어 검사를
 * 통과한 뒤 base URL 조립 단계에서 브라우저가 `..` 를 정규화해 타 상품 자산을 여는 우회가 남는다.
 * 세그먼트 전부를 화이트리스트로 고정해 `.`·`/` 가 끼어들 자리를 없앤다.
 */
export const ASSET_KEY_PATTERN = new RegExp(
  `^products/${UUID_PATTERN}/(${Object.values(CATALOG_UPLOAD_PURPOSE_SEGMENTS).join('|')})/${UUID_PATTERN}\\.(${Object.values(IMAGE_CONTENT_TYPE_EXTENSIONS).join('|')})$`,
);

/**
 * presign 이 발급한 S3 키만 되돌려받는다. 키는 서버가 생성하므로 클라이언트가
 * 임의 경로를 만들 이유가 없다 — 형식 전체를 고정해 경로 탈출을 차단한다.
 * 키가 실제로 해당 상품 소유(상품ID 세그먼트 일치)인지는 서버 유스케이스가 검증한다.
 */
export const assetKeySchema = z
  .string()
  .max(512)
  .regex(ASSET_KEY_PATTERN, '올바른 자산 키 형식이 아닙니다.');

export const assetRefSchema = z.object({
  key: z.string(),
  /** 표시용 URL. 비공개 버킷이라 presigned GET, CDN 도입 후엔 base URL 조립. */
  url: z.string(),
});
export type AssetRef = z.infer<typeof assetRefSchema>;

// ---------------------------------------------------------------------------
// 카테고리 — 2단계 트리(대분류/소분류). 깊이·동명 검증은 서비스 레이어.
// ---------------------------------------------------------------------------

/** 카테고리 이름 상한 — 어드민 입력 칸의 maxLength 도 이 값이다(넘친 값은 서버가 영어 검증 문구로 거부한다). */
export const MAX_CATEGORY_NAME_LENGTH = 50;
const categoryNameSchema = z.string().trim().min(1).max(MAX_CATEGORY_NAME_LENGTH);

export const createCategoryInputSchema = z.object({
  name: categoryNameSchema,
  /** null 이면 대분류, 값이 있으면 해당 대분류 아래 소분류. */
  parentId: uuidSchema.nullable().default(null),
});
export type CreateCategoryInput = z.infer<typeof createCategoryInputSchema>;

export const updateCategoryInputSchema = z.object({
  id: uuidSchema,
  name: categoryNameSchema,
});
export type UpdateCategoryInput = z.infer<typeof updateCategoryInputSchema>;

/** 노출 순서 변경은 형제 전체를 원하는 순서로 제출한다 (부분 스왑 금지 — 중간 상태 충돌 방지). */
export const reorderCategoriesInputSchema = z.object({
  parentId: uuidSchema.nullable(),
  orderedIds: z.array(uuidSchema).min(1),
});
export type ReorderCategoriesInput = z.infer<typeof reorderCategoriesInputSchema>;

export const categoryLeafSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  displayOrder: z.number().int(),
});
export type CategoryLeaf = z.infer<typeof categoryLeafSchema>;

export const categoryTreeNodeSchema = categoryLeafSchema.extend({
  children: z.array(categoryLeafSchema),
});
export type CategoryTreeNode = z.infer<typeof categoryTreeNodeSchema>;

export const categoryTreeSchema = z.array(categoryTreeNodeSchema);

// ---------------------------------------------------------------------------
// 인쇄 방식 — 전역 마스터. 사용 중 삭제는 DB RESTRICT 가 최종 방어.
// ---------------------------------------------------------------------------

/** 인쇄 방식 이름 상한 — 어드민 입력 칸의 maxLength 도 이 값이다(넘친 값은 서버가 영어 검증 문구로 거부한다). */
export const MAX_PRINT_METHOD_NAME_LENGTH = 50;
const printMethodNameSchema = z.string().trim().min(1).max(MAX_PRINT_METHOD_NAME_LENGTH);

/** 인쇄 방식 설명 상한 — 고객 상세의 프린트 가이드 본문. 어드민 입력 칸의 maxLength 도 이 값이다. */
export const MAX_PRINT_METHOD_DESCRIPTION_LENGTH = 500;
const printMethodDescriptionSchema = z
  .string()
  .trim()
  .max(MAX_PRINT_METHOD_DESCRIPTION_LENGTH)
  // 빈 입력은 "설명 없음"이다 — 빈 문자열을 저장하지 않는다.
  .transform((value) => (value === '' ? null : value))
  .nullable();

export const createPrintMethodInputSchema = z.object({
  name: printMethodNameSchema,
  description: printMethodDescriptionSchema.optional(),
});
export const updatePrintMethodInputSchema = z.object({
  id: uuidSchema,
  name: printMethodNameSchema,
});
/** 설명만 바꾼다 — 이름 수정(update)과 분리해 두 조작이 서로의 값을 덮어쓰지 않게 한다. */
export const setPrintMethodDescriptionInputSchema = z.object({
  id: uuidSchema,
  description: printMethodDescriptionSchema,
});
export type SetPrintMethodDescriptionInput = z.infer<typeof setPrintMethodDescriptionInputSchema>;

export const printMethodSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string().nullable(),
  /** 이 방식을 쓰는 상품 수 — 미사용 방식 정리의 판단 근거. */
  productCount: z.number().int().nonnegative(),
});
export type PrintMethod = z.infer<typeof printMethodSchema>;

export const printMethodListSchema = z.array(printMethodSchema);

/** 고객 프린트 가이드(printMethods.listGuide) — 전체 인쇄 방식의 이름·설명, 이름순. 비로그인 허용. */
export const printMethodGuideItemSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string().nullable(),
});
export type PrintMethodGuideItem = z.infer<typeof printMethodGuideItemSchema>;
export const printMethodGuideSchema = z.array(printMethodGuideItemSchema);

// ---------------------------------------------------------------------------
// 상품 — 생성은 기본 정보 완성 시(스키마 NOT NULL 과 일치), 이후 섹션별 저장.
// ---------------------------------------------------------------------------

export const MAX_PRODUCT_NAME_LENGTH = 100;
export const MAX_PRODUCT_NOTE_LENGTH = 2000;

/**
 * 기본정보 자유 문장(납기·소재·제작 조건). 문구는 admin basic-info-form 이 그대로 보이므로
 * 폼 라벨을 받아 어느 칸인지 드러낸다.
 */
const productNoteSchemaOf = (label: string) =>
  z
    .string()
    .trim()
    .min(1, `${label}를 입력해 주세요.`)
    .max(MAX_PRODUCT_NOTE_LENGTH, `${label}는 ${MAX_PRODUCT_NOTE_LENGTH}자 이하로 입력해 주세요.`);

const PRODUCT_NAME_LENGTH_MESSAGE = `상품명은 1~${MAX_PRODUCT_NAME_LENGTH}자로 입력해 주세요.`;

/** 상품 실측 mm 상한 — 편집영역 MAX_COMPONENT_MM 과 같은 값이어야 한다(catalog.spec 이 검사). */
export const MAX_PRODUCT_SIZE_MM = 5000;

/**
 * 원통 지름 상한 — 반둘레 프레임(π × 지름 / 2)이 편집영역 mm 상한을 넘지 않는 소수 둘째 자리 최대값.
 * 넘으면 그 상품의 편집영역 서피스 실측을 저장할 수 없다.
 */
export const MAX_CYLINDER_DIAMETER_MM = Math.floor((2 * MAX_PRODUCT_SIZE_MM * 100) / Math.PI) / 100;

/**
 * 실측 하한 — DECIMAL(9,2) 가 담을 수 있는 최소 양수다. 이보다 작은 양수는 컬럼에서 0.00 으로
 * 반올림되어 chk_products_width_mm_range(`width_mm > 0`)를 위반하므로 계약에서 먼저 막는다.
 */
export const MIN_PRODUCT_SIZE_MM = 0.01;

const HUNDREDTHS_TOLERANCE = 1e-6;

/** products.width_mm·height_mm (DECIMAL(9,2)) 에 그대로 들어가는 값. 문구는 운영자에게 그대로 보인다. */
export const productSizeMmSchema = z
  .number('실측을 숫자로 입력해 주세요.')
  .min(MIN_PRODUCT_SIZE_MM, `실측은 ${MIN_PRODUCT_SIZE_MM} mm 이상이어야 합니다.`)
  .max(MAX_PRODUCT_SIZE_MM, `실측은 ${MAX_PRODUCT_SIZE_MM} mm 이하여야 합니다.`)
  .refine((value) => Math.abs(value * 100 - Math.round(value * 100)) < HUNDREDTHS_TOLERANCE, {
    message: '실측은 소수 둘째 자리까지 입력할 수 있습니다.',
  });

export const createProductInputSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, PRODUCT_NAME_LENGTH_MESSAGE)
      .max(MAX_PRODUCT_NAME_LENGTH, PRODUCT_NAME_LENGTH_MESSAGE),
    minOrderQty: z
      .number('최소 주문 수량을 숫자로 입력해 주세요.')
      .int('최소 주문 수량은 정수여야 합니다.')
      .min(1, '최소 주문 수량은 1 이상이어야 합니다.')
      .max(
        MAX_MIN_ORDER_QTY,
        `최소 주문 수량은 ${MAX_MIN_ORDER_QTY.toLocaleString('ko-KR')} 이하여야 합니다.`,
      ),
    leadTimeNote: productNoteSchemaOf('납기 안내'),
    materialNote: productNoteSchemaOf('소재 정보'),
    measurementKind: z.enum(PRODUCT_MEASUREMENT_KINDS, '실측 종류를 골라 주세요.'),
    /** 제품 실측 넓이(원통이면 지름)·높이(mm). 편집영역 표면 실측의 원천(정책 §1 프레임). */
    widthMm: productSizeMmSchema,
    heightMm: productSizeMmSchema,
    productionNote: productNoteSchemaOf('제작 조건 안내'),
  })
  .superRefine((input, ctx) => {
    if (input.measurementKind === 'CYLINDER' && input.widthMm > MAX_CYLINDER_DIAMETER_MM) {
      ctx.addIssue({
        code: 'custom',
        path: ['widthMm'],
        message: `원통 지름은 ${MAX_CYLINDER_DIAMETER_MM} mm 이하여야 합니다.`,
      });
    }
  });
export type CreateProductInput = z.infer<typeof createProductInputSchema>;

export const createProductResultSchema = z.object({ id: uuidSchema });

export const updateProductBasicInfoInputSchema = createProductInputSchema.extend({
  id: uuidSchema,
});
export type UpdateProductBasicInfoInput = z.infer<typeof updateProductBasicInfoInputSchema>;

/** 낮을수록 앞. null = 비추천. 10 단위 간격 운영 권장. */
export const updateRecommendedRankInputSchema = z.object({
  id: uuidSchema,
  recommendedRank: z.number().int().min(1).max(MAX_RECOMMENDED_RANK).nullable(),
});

export const setProductCategoriesInputSchema = z
  .object({
    id: uuidSchema,
    /** 소분류만 연결 가능(서비스 검증). 공개 상품은 연결 >= 1 불변식. */
    categoryIds: z.array(uuidSchema).min(1),
    primaryCategoryId: uuidSchema,
  })
  .refine((input) => input.categoryIds.includes(input.primaryCategoryId), {
    message: '대표 카테고리는 연결된 카테고리 중 하나여야 합니다.',
    path: ['primaryCategoryId'],
  });
export type SetProductCategoriesInput = z.infer<typeof setProductCategoriesInputSchema>;

export const setProductPrintMethodsInputSchema = z.object({
  id: uuidSchema,
  printMethodIds: z.array(uuidSchema).max(20),
});

export const setProductImagesInputSchema = z.object({
  id: uuidSchema,
  /** 배열 순서 = 노출 순서. 1번이 썸네일. */
  imageKeys: z.array(assetKeySchema).max(MAX_PRODUCT_IMAGES),
  detailImageKey: assetKeySchema.nullable(),
});
export type SetProductImagesInput = z.infer<typeof setProductImagesInputSchema>;

export const colorCodeSchema = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, '색상 코드는 #RRGGBB 형식이어야 합니다.');

/** 색상명 상한 — 어드민 입력 칸의 maxLength 도 이 값이다(넘친 값은 서버가 영어 검증 문구로 거부한다). */
export const MAX_COLOR_OPTION_NAME_LENGTH = 50;

export const colorOptionInputSchema = z.object({
  /** 있으면 기존 행 갱신, 없으면 신규. 목록에서 빠진 기존 행은 삭제된다(동기화). */
  id: uuidSchema.optional(),
  name: z.string().trim().min(1).max(MAX_COLOR_OPTION_NAME_LENGTH),
  colorCode: colorCodeSchema,
  colorFamily: z.enum(COLOR_FAMILIES),
  isAvailable: z.boolean(),
  /** null 이면 상세에서 대표사진 1번 폴백. */
  previewImageKey: assetKeySchema.nullable(),
});
export type ColorOptionInput = z.infer<typeof colorOptionInputSchema>;

export const setProductColorOptionsInputSchema = z
  .object({
    id: uuidSchema,
    colorOptions: z.array(colorOptionInputSchema).max(MAX_COLOR_OPTIONS),
  })
  .refine(
    (input) => {
      const definedIds = input.colorOptions
        .map((option) => option.id)
        .filter((id): id is string => id !== undefined);

      return new Set(definedIds).size === definedIds.length;
    },
    { message: '같은 색상 옵션이 중복되었습니다.', path: ['colorOptions'] },
  );
export type SetProductColorOptionsInput = z.infer<typeof setProductColorOptionsInputSchema>;

/** 사이즈 옵션 상한 — 상품당 20개. */
export const MAX_SIZE_OPTIONS = 20;
/** 사이즈 이름 상한 — 어드민 입력 칸의 maxLength 도 이 값이다. */
export const MAX_SIZE_OPTION_NAME_LENGTH = 20;

export const sizeOptionInputSchema = z.object({
  /**
   * 있으면 기존 행 갱신, 없으면 신규. 목록에서 빠진 기존 행은 삭제된다(동기화). 소문자로 정규화해 대소문자만 다른 같은 id 도
   * 아래 중복 검사에 걸리게 한다.
   */
  id: normalizedUuidSchema.optional(),
  name: z.string().trim().min(1).max(MAX_SIZE_OPTION_NAME_LENGTH),
});
export type SizeOptionInput = z.infer<typeof sizeOptionInputSchema>;

export const setProductSizeOptionsInputSchema = z
  .object({
    id: uuidSchema,
    sizeOptions: z.array(sizeOptionInputSchema).max(MAX_SIZE_OPTIONS),
  })
  .superRefine((input, ctx) => {
    const definedIds = input.sizeOptions
      .map((option) => option.id)
      .filter((id): id is string => id !== undefined);
    if (new Set(definedIds).size !== definedIds.length)
      ctx.addIssue({
        code: 'custom',
        path: ['sizeOptions'],
        message: '같은 사이즈 옵션이 중복되었습니다.',
      });
    const names = input.sizeOptions.map((option) => option.name);
    if (new Set(names).size !== names.length)
      ctx.addIssue({
        code: 'custom',
        path: ['sizeOptions'],
        message: '같은 사이즈가 중복되었습니다.',
      });
  });
export type SetProductSizeOptionsInput = z.infer<typeof setProductSizeOptionsInputSchema>;

export const productSizeOptionSchema = z.object({ id: z.uuid(), name: z.string() });
export type ProductSizeOption = z.infer<typeof productSizeOptionSchema>;

export const toggleColorAvailabilityInputSchema = z.object({
  colorOptionId: uuidSchema,
  isAvailable: z.boolean(),
});

export const priceTierInputSchema = z.object({
  minQty: z.number().int().min(1).max(MAX_MIN_ORDER_QTY),
  unitPrice: unitPriceSchema,
});

/** 첫 구간 minQty == 상품 minOrderQty 는 상품과 대조해야 하므로 서비스 레이어 검증. */
export const setPriceTiersInputSchema = z
  .object({
    id: uuidSchema,
    tiers: z.array(priceTierInputSchema).min(1).max(MAX_PRICE_TIERS),
  })
  .refine((input) => new Set(input.tiers.map((tier) => tier.minQty)).size === input.tiers.length, {
    message: '수량 구간이 중복됩니다.',
    path: ['tiers'],
  });
export type SetPriceTiersInput = z.infer<typeof setPriceTiersInputSchema>;

export const listProductsInputSchema = paginationSchema.extend({
  status: z.enum(PRODUCT_STATUSES).optional(),
  search: z.string().trim().min(1).max(100).optional(),
  /** 카테고리로 거른다 — 대분류면 그 아래 소분류에 묶인 상품까지 포함한다. */
  categoryId: uuidSchema.optional(),
});
export type ListProductsInput = z.infer<typeof listProductsInputSchema>;

// ---------------------------------------------------------------------------
// 상품 출력
// ---------------------------------------------------------------------------

export const productSummarySchema = z.object({
  id: z.uuid(),
  status: z.enum(PRODUCT_STATUSES),
  name: z.string(),
  minOrderQty: z.number().int(),
  /** 최소 주문 수량 구간의 개당 단가(원). DB 트리거가 재계산하는 파생값. */
  baseUnitPrice: z.number().int().nullable(),
  recommendedRank: z.number().int().nullable(),
  thumbnail: assetRefSchema.nullable(),
  primaryCategoryName: z.string().nullable(),
  updatedAt: z.iso.datetime(),
});
export type ProductSummary = z.infer<typeof productSummarySchema>;

export const productListSchema = paginatedSchema(productSummarySchema);

export const productCategoryLinkSchema = z.object({
  categoryId: z.uuid(),
  name: z.string(),
  isPrimary: z.boolean(),
});

export const productColorOptionSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  colorCode: z.string(),
  colorFamily: z.enum(COLOR_FAMILIES),
  isAvailable: z.boolean(),
  previewImage: assetRefSchema.nullable(),
});
export type ProductColorOption = z.infer<typeof productColorOptionSchema>;

export const productPriceTierSchema = z.object({
  id: z.uuid(),
  minQty: z.number().int(),
  unitPrice: z.number().int(),
});

export const productImageSchema = assetRefSchema.extend({
  displayOrder: z.number().int(),
});

export const productDetailSchema = z.object({
  id: z.uuid(),
  status: z.enum(PRODUCT_STATUSES),
  name: z.string(),
  minOrderQty: z.number().int(),
  leadTimeNote: z.string(),
  materialNote: z.string(),
  measurementKind: z.enum(PRODUCT_MEASUREMENT_KINDS),
  /** 실측 컬럼 도입 전에 만든 상품은 null 이다. 실측 종류가 CYLINDER 면 지름이다. */
  widthMm: z.number().nullable(),
  heightMm: z.number().nullable(),
  productionNote: z.string(),
  detailImage: assetRefSchema.nullable(),
  baseUnitPrice: z.number().int().nullable(),
  recommendedRank: z.number().int().nullable(),
  images: z.array(productImageSchema),
  categories: z.array(productCategoryLinkSchema),
  printMethodIds: z.array(z.uuid()),
  colorOptions: z.array(productColorOptionSchema),
  sizeOptions: z.array(productSizeOptionSchema),
  priceTiers: z.array(productPriceTierSchema),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type ProductDetail = z.infer<typeof productDetailSchema>;

// ---------------------------------------------------------------------------
// 발행 가드 — PUBLISHED 전이 조건. 실패 시 미충족 조건 전체를 반환해
// UI 가 체크리스트로 표시한다.
// ---------------------------------------------------------------------------

export const PUBLISH_BLOCKERS = ['NO_CATEGORY', 'NO_PRICE_TIER', 'NO_MEASUREMENT'] as const;
export type PublishBlocker = (typeof PUBLISH_BLOCKERS)[number];

export const publishProductResultSchema = z.object({
  published: z.boolean(),
  unmetConditions: z.array(z.enum(PUBLISH_BLOCKERS)),
});
export type PublishProductResult = z.infer<typeof publishProductResultSchema>;
